import { FormattedMessage, useIntl } from "react-intl";
import { describesRole } from "@/components/match/match-mode";
import type { ToMatchMode } from "../dashboard-kinds";
import { PowerProfileEditor } from "@/components/match/PowerProfileEditor";
import {
  DEFAULT_TEAM_PROFILE_ELEMENT_WEIGHTS,
  DEFAULT_TEAM_PROFILE_ID,
} from "@/components/match/match-design-rules";
import type { MatchData } from "@/components/match/match-design-types";
import {
  MatchSkillsDiagram,
  type MatchSkillEntry,
  type MatchSkillRequirement,
} from "@/components/match/requirements/MatchSkillsDiagram";
import { SubsectionHeader } from "@/components/match/requirements/row-primitives";
import type { ProjectExpertiseRequirement, ProjectRequirements } from "../project-plan";

// Ein Team wird nicht über Namen beschrieben, sondern über Fähigkeiten:
// Fachkompetenzen auf der 7er-Leiter plus die Powers, die das Vorhaben braucht. Genau
// deshalb stehen hier die ECHTEN Bausteine aus Match (MatchSkillsDiagram,
// PowerProfileEditor) und keine eigene Wortskala - der Bedarf eines Projekts und
// das Können einer Person müssen im selben Vokabular stehen, sonst kann das
// Match sie nicht gegeneinander rechnen.
//
// Damit ist dieser Schritt zugleich die Grundlage eines späteren Matches: was
// hier entsteht, ist ein Anforderungsprofil, kein Personenwunsch.
//
// Ein neues Projekt beginnt leer. Vorher standen hier vier erfundene Rollen samt
// der Behauptung, sie seien aus den Abschnitten und einer Ausschreibung gelesen
// worden - gelesen hat nie etwas, und ein Bedarf, den niemand angemeldet hat,
// ist die unangenehmste Sorte Vorgabe: man übersieht sie und sucht danach.
//
// Beide Spalten beginnen auf DERSELBEN Linie und tragen dieselbe Art
// Überschrift. Vorher stand links eine Textzeile und rechts ein Aufklapper mit
// eigenem Rahmen: zwei gleichrangige Hälften, die unterschiedlich aussahen und
// deren Köpfe um ein paar Pixel gegeneinander versetzt waren. Eine Box in der
// Box verbietet UI_CONVENTIONS ohnehin.
//
// Fachkompetenzen und Powers stehen nebeneinander, sobald Platz ist: es sind die
// zwei Hälften EINER Aussage ("was muss das Team können, wie soll es arbeiten"),
// und untereinander liest man die zweite erst nach dem Scrollen. Unterhalb von
// @5xl stapeln sie, weil zwei zu schmale Spalten schlechter sind als eine gute.
//
// Die Breitengrenze bleibt: die Bausteine kommen aus Match und sind für dessen
// @lg-Zweispalter (ChallengeStep, rund 740px) entworfen. Über die volle
// Seitenbreite gezogen wird die Fachkompetenz-Leiter zur Landebahn und die
// aspect-square-Power-Kacheln wachsen von ~140px auf ~280px Höhe.

const uid = () => globalThis.crypto.randomUUID();

/** Die Leiter arbeitet mit MatchSkillEntry; das ist dieselbe Form ohne die Projekt-Id. */
function toEntries(requirements: ProjectRequirements, personCount: boolean): MatchSkillEntry[] {
  return requirements.expertise.map((r) => ({
    id: r.id,
    description: r.description,
    level: r.level,
    requirement: r.requirement satisfies MatchSkillRequirement,
    ...(personCount && r.personCount !== undefined ? { personCount: r.personCount } : {}),
  }));
}

export function ProjectGuidedTeamScreen({
  requirements,
  onChange,
  readOnly = false,
  mode = "OPTIMIZE",
}: {
  requirements: ProjectRequirements;
  onChange: (next: ProjectRequirements) => void;
  /** Nur lesen (`projectRights`), mit dem Nur-Lese-Zustand der Match-Bausteine. */
  readOnly?: boolean;
  mode?: ToMatchMode;
}) {
  const intl = useIntl();
  const carriesPersonCount = !describesRole(mode);
  const entries = toEntries(requirements, carriesPersonCount);

  // Das Profil ist immer "Individuell": ein Projekt gewichtet seine Powers
  // selbst, die benannten Vorlagen gehören ins Match.
  //
  // Ohne gespeicherte Gewichte zeigt der Editor die neutrale Vorgabe, nicht
  // lauter Nullen: eine Scorecard, auf der jeder Balken unten liegt, sieht nach
  // "alles unwichtig" aus, gemeint ist aber "noch nicht gewichtet". Gespeichert
  // wird trotzdem erst, wenn jemand dreht - im Dokument steht bis dahin nichts.
  const profile: MatchData["teamProfile"] = {
    id: DEFAULT_TEAM_PROFILE_ID,
    name: intl.formatMessage({ id: "project.guided.team.profileName", defaultMessage: "Individuell" }),
    elementWeights:
      Object.keys(requirements.elementWeights).length > 0
        ? requirements.elementWeights
        : { ...DEFAULT_TEAM_PROFILE_ELEMENT_WEIGHTS },
  };

  function setExpertise(next: readonly ProjectExpertiseRequirement[]) {
    onChange({ ...requirements, expertise: carriesPersonCount ? next : next.map(({ id, description, level, requirement }) => ({ id, description, level, requirement })) });
  }

  function patch(id: string, next: Partial<ProjectExpertiseRequirement>) {
    setExpertise(requirements.expertise.map((e) => (e.id === id ? { ...e, ...next } : e)));
  }

  return (
    <div>
      <p className="smycles-field-meta max-w-[62ch] text-ink">
        <FormattedMessage
          id="project.guided.team.sub"
          defaultMessage="Gesucht werden Fähigkeiten, nicht Namen. Was hier steht, ist dasselbe Profil, mit dem später ein Match rechnet."
        />
      </p>

      <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-6 @5xl:grid-cols-2">
      <div>
        <SubsectionHeader
          title={intl.formatMessage({
            id: "project.guided.team.expertiseTitle",
            defaultMessage: "Benötigte Fachkompetenzen (mind.)",
          })}
        />
        <MatchSkillsDiagram
          entries={entries}
          personCount={carriesPersonCount}
          readOnly={readOnly}
          onPlace={(id, level) => patch(id, { level })}
          onToggleRequirement={(id, requirement) => patch(id, { requirement })}
          onAdd={(description, level, requirement, personCount) =>
            setExpertise([
              ...requirements.expertise,
              { id: uid(), description, level, requirement, ...(personCount === undefined ? {} : { personCount }) },
            ])
          }
          onRename={(id, description) => patch(id, { description })}
          onDelete={(id) => setExpertise(requirements.expertise.filter((e) => e.id !== id))}
          onPersonCountChange={carriesPersonCount ? (id, personCount) => patch(id, { personCount }) : undefined}
        />
      </div>

      <div>
        {/* Die Bedienhilfe steht als Hinweis IM Kopf, nicht als Absatz darunter:
            sonst beginnt der Inhalt der rechten Spalte eine Zeile tiefer als der
            der linken, und die beiden Hälften laufen sichtbar auseinander. */}
        <SubsectionHeader
          title={intl.formatMessage({
            id: "project.guided.team.powersTitle",
            defaultMessage: "Power-Scorecard",
          })}
          hint={
            readOnly
              ? undefined
              : intl.formatMessage({
                  id: "project.guided.team.powersSub",
                  defaultMessage: "Klick eine Gruppe an, um ihre fünf Powers zu gewichten.",
                })
          }
        />
        <PowerProfileEditor
          profile={profile}
          onChange={(next) => {
            onChange({ ...requirements, elementWeights: next?.elementWeights ?? {} });
          }}
          idPrefix="project-mock-powers"
          readOnly={readOnly}
        />
      </div>

      </div>

    </div>
  );
}
